import clsx from 'clsx';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  AdminQueryUser,
  adminQueryUserIsBlank,
  queryUser,
} from '../../../api/admin';
import { AuthStateSignedIn } from '../../../auth/AuthProvider';
import { log } from '../../../logger';
import { stringifyError } from '../../../utils/core';
import { ADMIN_QUERY_PARAM } from '../utils';
import AdminUser from './AdminUser';
import Button from './Button';

type AdminMainProps = Pick<RouteComponentProps, 'location' | 'history'> &
  Pick<AuthStateSignedIn, 'user'> & {
    className?: string;
  };

type SearchUserBase = { search: string };
type SearchUserLoading = SearchUserBase & { type: 'loading' };
type SearchUserLoaded = SearchUserBase & {
  type: 'loaded';
  user?: AdminQueryUser;
  error?: string;
};
type SearchUser = SearchUserLoading | SearchUserLoaded;

const AdminMain: FunctionComponent<AdminMainProps> = ({
  className,
  location,
  history,
  //   user,
}) => {
  const params = new URLSearchParams(location.search);

  const paramsSearch = params.get(ADMIN_QUERY_PARAM) || '';
  const [search, setSearch] = useState(paramsSearch);
  const [searchUser, setSearchUser] = useState<SearchUser | null>(null);

  const isSubmitting = useRef(false);

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    if (isSubmitting.current) {
      log.warn('Already submitting');
      return;
    }

    params.set(ADMIN_QUERY_PARAM, search);
    history.push(`${location.pathname}?${params.toString()}`);
  };

  const _doUserQuery = async () => {
    const search = paramsSearch;

    setSearch(search); // make sure input is updated too

    if (!search) {
      return;
    }

    if (search.length <= 2) {
      setSearchUser({ type: 'loaded', search, error: 'Search too short.' });
      return;
    }

    isSubmitting.current = true;
    setSearchUser({ type: 'loading', search });

    return queryUser(search)
      .then((searchUser) => {
        isSubmitting.current = false;
        setSearchUser({ type: 'loaded', search, user: searchUser });
      })
      .catch((err) => {
        isSubmitting.current = false;
        setSearchUser({
          type: 'loaded',
          search,
          user: undefined,
          error: stringifyError(err),
        });
      });
  };

  useEffect(() => {
    _doUserQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsSearch]);

  return (
    <div className={clsx('flex flex-col', className)}>
      <form
        className="flex shrink-0 grow-0 items-center justify-start space-x-1 border-0 border-b border-solid border-b-gray-200 bg-gray-100 px-5 py-4"
        onSubmit={handleSubmit}
      >
        <input
          className="shrink grow rounded-md border border-solid border-gray-300 bg-white px-2 py-[3px] leading-5"
          type="text"
          placeholder="email or userId or orgId"
          value={search}
          onChange={(evt) => {
            const { target } = evt;
            setSearch(target.value);
          }}
        />
        <Button className="shrink-0 !rounded-md" type="submit">
          Search
        </Button>
      </form>
      <div className="h-0 grow overflow-y-auto">
        {searchUser ? (
          searchUser.type === 'loading' ? (
            <div className="px-5 py-4">Loading…</div>
          ) : (
            <div>
              {searchUser.error ? (
                <div className="bg-red-100 px-5 py-4 text-red-600">
                  {searchUser.error}
                </div>
              ) : null}
              {adminQueryUserIsBlank(searchUser.user) ? (
                <div className="px-5 py-4">No user.</div>
              ) : (
                <AdminUser
                  className={clsx(
                    'px-5 py-4',
                    paramsSearch !== search && 'opacity-50',
                  )}
                  user={searchUser.user!}
                  requeryUser={_doUserQuery}
                />
              )}
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

AdminMain.displayName = 'AdminMain';

export default AdminMain;
